import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { AuthService } from './auth.service';
import { LayoutService } from '@/layout/service/layout.service';

const VERIFIER_KEY = 'kc.verifier';
const STATE_KEY = 'kc.state';
const RETURN_URL_KEY = 'kc.returnUrl';

export type ResultadoLogin = 'nao-callback' | 'ok' | 'sem-acesso' | 'falha';

/**
 * Monta os parâmetros do Authorization Code + PKCE do login pelo Keycloak.
 * SEM selected_login_type → o usuário cai na página padrão do Keycloak
 * (CPF+senha + certificado + reconhecimento de sessão SSO).
 */
export function montarParametrosLogin(opts: { clientId: string; redirectUri: string; challenge: string; state: string }): Record<string, string> {
    return {
        response_type: 'code',
        client_id: opts.clientId,
        redirect_uri: opts.redirectUri,
        scope: 'openid profile email',
        code_challenge: opts.challenge,
        code_challenge_method: 'S256',
        state: opts.state
    };
}

/**
 * Login pelo Keycloak (Authorization Code + PKCE manual). Inicia o redirect e, no retorno,
 * troca o code pelo backend (/api/auth/callback), que seta o cookie de refresh. Recupera o
 * SSO entre sistemas CFM. A página do Keycloak oferece CPF+senha e certificado.
 * Não usa angular-oauth2-oidc.
 */
@Injectable({ providedIn: 'root' })
export class KeycloakLoginService {
    private readonly http = new HttpClient(inject(HttpBackend));
    private readonly authService = inject(AuthService);
    private readonly layoutService = inject(LayoutService);

    async iniciar(returnUrl: string = '/'): Promise<void> {
        const verifier = this.aleatorioHex(32);
        const state = this.aleatorioHex(16);
        const challenge = await this.challengeS256(verifier);
        sessionStorage.setItem(VERIFIER_KEY, verifier);
        sessionStorage.setItem(STATE_KEY, state);
        sessionStorage.setItem(RETURN_URL_KEY, returnUrl);

        const params = new URLSearchParams(
            montarParametrosLogin({
                clientId: environment.clientId,
                redirectUri: environment.redirectUri,
                challenge,
                state
            })
        );
        window.location.href = `${environment.keycloak}/protocol/openid-connect/auth?${params.toString()}`;
    }

    /**
     * Encerra a sessão SSO no Keycloak (RP-initiated logout / end-session): limpa o cookie
     * de sessão no domínio do Keycloak e volta ao app pelo post_logout_redirect_uri, de onde
     * um novo login é iniciado do zero. Usado no "Entrar novamente" após uma falha de login.
     */
    logoutRedirect(): void {
        const params = new URLSearchParams({
            client_id: environment.clientId,
            post_logout_redirect_uri: environment.postLogoutRedirectUri
        });
        window.location.href = `${environment.keycloak}/protocol/openid-connect/logout?${params.toString()}`;
    }

    async completarSeRetorno(): Promise<ResultadoLogin> {
        const url = new URLSearchParams(window.location.search);
        const code = url.get('code');
        const state = url.get('state');
        if (!code || !state) {
            return 'nao-callback';
        }
        const verifier = sessionStorage.getItem(VERIFIER_KEY);
        const stateSalvo = sessionStorage.getItem(STATE_KEY);
        sessionStorage.removeItem(VERIFIER_KEY);
        sessionStorage.removeItem(STATE_KEY);
        if (!verifier || state !== stateSalvo) {
            return 'falha';
        }
        try {
            const resp = await firstValueFrom(this.http.post<{ access_token: string }>(`${environment.apis.apiSicom}/api/auth/callback`, { code, codeVerifier: verifier, redirectUri: environment.redirectUri }, { withCredentials: true }));
            await this.authService.aplicarSessaoExterna(resp.access_token);
            // Login fresco: o init() do layout rodou no boot (sem token) e deu bail.
            // Com a sessão setada, recarrega tema/animação do web_layout do usuário.
            await this.layoutService.init();
            return 'ok';
        } catch (e) {
            return (e as { status?: number }).status === 403 ? 'sem-acesso' : 'falha';
        }
    }

    consumirReturnUrl(): string {
        const url = sessionStorage.getItem(RETURN_URL_KEY) ?? '/';
        sessionStorage.removeItem(RETURN_URL_KEY);
        return url;
    }

    private aleatorioHex(bytes: number): string {
        const arr = new Uint8Array(bytes);
        crypto.getRandomValues(arr);
        return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
    }

    private async challengeS256(verifier: string): Promise<string> {
        const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
        let bin = '';
        new Uint8Array(hash).forEach((b) => (bin += String.fromCharCode(b)));
        return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
}
